import { KeyOutlined, MobileOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { Button, Flex, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthProvider';
import { BrandMark } from '../components/BrandMark';
import { brandRamp, neutral, shadow } from '../theme';
import { ThemeToggle } from '../themeMode';
import { LanguagePicker } from './SignInPage';

function Step({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <Flex gap={12} align="flex-start">
      <span
        aria-hidden
        style={{
          flex: 'none',
          display: 'inline-grid',
          placeItems: 'center',
          width: 32,
          height: 32,
          borderRadius: 8,
          background: brandRamp[160],
          color: brandRamp[80],
          fontSize: 16,
        }}
      >
        {icon}
      </span>
      <Typography.Text style={{ paddingTop: 5 }}>{children}</Typography.Text>
    </Flex>
  );
}

/**
 * Shown to an administrator whose session has not met two-step verification (ADR-0006): why it
 * is asked, what the next pages will do, and the button that signs in again through Keycloak,
 * which then has them set up an authenticator application. Same card as the sign-in page.
 */
export function TwoStepRequiredPage() {
  const { t } = useTranslation();
  const { reauthenticate, signOut } = useAuth();
  return (
    <Flex
      vertical
      align="center"
      justify="center"
      className="wm-signin"
      style={{
        minHeight: '100vh',
        padding: '48px 16px 64px',
        background: `radial-gradient(900px 520px at 12% 8%, ${brandRamp[160]} 0%, transparent 70%), radial-gradient(800px 520px at 92% 96%, var(--wm-signin-glow) 0%, transparent 70%), ${neutral.background3}`,
      }}
    >
      <main
        className="wm-signin-card"
        style={{
          width: '100%',
          maxWidth: 480,
          boxSizing: 'border-box',
          padding: 44,
          background: neutral.background1,
          borderRadius: 4,
          boxShadow: shadow.shadow16,
        }}
      >
        <Flex align="center" gap={10} style={{ marginBottom: 24 }}>
          <BrandMark size={28} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, lineHeight: '20px' }}>
              {t('app.brand')}
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12, lineHeight: '16px' }}>
              {t('app.tagline')}
            </Typography.Text>
          </div>
        </Flex>
        <Typography.Title level={4} style={{ margin: 0, fontWeight: 600 }}>
          {t('twoStep.title')}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ margin: '8px 0 20px' }}>
          {t('twoStep.lead')}
        </Typography.Paragraph>
        <Flex vertical gap={14} style={{ marginBottom: 20 }}>
          <Step icon={<MobileOutlined />}>{t('twoStep.step1')}</Step>
          <Step icon={<SafetyCertificateOutlined />}>{t('twoStep.step2')}</Step>
          <Step icon={<KeyOutlined />}>{t('twoStep.step3')}</Step>
        </Flex>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 24 }}>
          {t('twoStep.passkeyHint')}
        </Typography.Paragraph>
        <Flex justify="flex-end" gap={8} wrap>
          <Button size="large" onClick={() => void signOut()}>
            {t('app.signOut')}
          </Button>
          <Button type="primary" size="large" onClick={() => void reauthenticate()}>
            {t('twoStep.continue')}
          </Button>
        </Flex>
        <Flex align="center" gap={4} style={{ marginTop: 24, marginInlineStart: -8 }}>
          <LanguagePicker />
          <ThemeToggle />
        </Flex>
      </main>
    </Flex>
  );
}
